import { HttpClient, HttpErrorResponse, HttpHeaders, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, throwError } from 'rxjs';
import { SaisieInfosSouscripteur } from 'src/app/core/models/form-state.model';
import { MSAL_TOKEN, TOKEN } from 'src/app/shared/constants/localstorage.constants';
import { Configuration } from '../configuration';
import { MemberData } from '../models/member.model';

@Injectable({
  providedIn: 'root'
})
export class MemberService {
  basePath: string;
  constructor(private readonly http: HttpClient, configuration: Configuration) {
    this.basePath = configuration.apiBasePath ?? '';
  }

  public getByEmail(email: string): Observable<MemberData> {
    const header = this.buildAuthHeader(MSAL_TOKEN);
    const url = `${this.basePath}members/${email}`;
    return this.http.get<MemberData & { cellPhone: string }>(url, { headers: header }).pipe(
      map(data => {
        // Remap some fields with different names from the endpoint and add the email from the method input
        data.email = email;
        data.phoneNumber = data.cellPhone;
        return data;
      })
    );
  }

  public getUserInfoAuth(): Observable<MemberData> {
    const header = this.buildAuthHeader(TOKEN);
    return this.http
      .get<MemberData & { civilityId: number; cellPhone: string }>(`${this.basePath}members/me`, {
        headers: header
      })
      .pipe(
        map(data => {
          // Remap some fields with different names from the endpoint
          data.civilityTypeId = data.civilityId;
          return data;
        })
      );
  }

  public emailExists(email: string): Observable<boolean> {
    const header = this.buildAuthHeader(MSAL_TOKEN);
    const url = `${this.basePath}members/${email}`;
    return this.http.head(url, { observe: 'response', headers: header }).pipe(
      map(response => {
        if (response.status === 200) {
          return true;
        } else if (response.status === 204) {
          return false;
        } else {
          throw new Error(`Unexpected status code: ${response.status}`);
        }
      }),
      catchError(error => {
        console.error('Error:', error);
        return throwError('Error occurred during HTTP request.');
      })
    );
  }

  public isEnrolled(email: string): Observable<boolean> {
    return this.http.get<boolean>(`${this.basePath}members/${email}/enrollmentStatus`);
  }

  public sendEmailInitEspaceAdherent(email: string, type: string): Observable<void> {
    const encodedEmail = encodeURIComponent(email);
    return this.http.post<void>(
      `${this.basePath}members/member:send?email=${encodedEmail}&type=${type}`,
      {}
    );
  }

  public emailIsValid(email: string): Observable<boolean> {
    const url = `${this.basePath}members/${email}/validate`;
    return this.http.get(url, { observe: 'response' }).pipe(
      map(response => {
        if (response.status === HttpStatusCode.Ok) {
          console.debug('Email validation succeeded');
          return true;
        } else {
          console.error(`Unexpected status code: ${response.status}`);
          return false;
        }
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === HttpStatusCode.UnprocessableEntity) {
          console.error('Email validation failed:', email);
          return of(false);
        }

        console.error('Error:', error);
        return throwError(() => new Error('Error occurred during HTTP request.'));
      })
    );
  }

  public passwordReset(email: string): Observable<void> {
    return this.http.post<void>(`${this.basePath}members/passwordReset`, { email });
  }

  private buildAuthHeader(storageKey: string): HttpHeaders {
    const token = JSON.parse(localStorage.getItem(storageKey) ?? '""');
    let header: HttpHeaders = new HttpHeaders();
    if (token) {
      header = new HttpHeaders({
        Authorization: `Bearer ${token}`
      });
    }
    return header;
  }

  public getMemberIbans(): Observable<string[]> {
    const header = this.buildAuthHeader(TOKEN);
    return this.http.get<string[]>(`${this.basePath}members/me/ibans`, {
      headers: header
    });
  }

  public getMemberByEmailIbans(email: string): Observable<string[]> {
    const header = this.buildAuthHeader(MSAL_TOKEN);
    return this.http.get<string[]>(`${this.basePath}members/${email}/ibans`, {
      headers: header
    });
  }

  public getMemberAge(email: string, isForChild: boolean): Observable<boolean> {
    return this.http.get<boolean>(`${this.basePath}members/${email}/isEligibleHE/${isForChild}`);
  }
}
